import React, { useEffect, useMemo, useCallback } from 'react';
import {
    Splitter, Tree, Input, Segmented, Empty, Spin, Typography, ConfigProvider,
} from 'antd';
import {
    SearchOutlined, FolderOutlined, FolderOpenOutlined,
    MessageOutlined, BranchesOutlined,
} from '@ant-design/icons';
import useTemplateCatalogStore from '../../stores/useTemplateCatalogStore';
import SequenceCard from './SequenceCard';
import { SECTIONS, CATEGORY_LABELS } from './categories';

const { Text } = Typography;

const CatalogBrowser = ({ isAdmin }) => {
    const {
        catalogLoading,
        categories,
        selectedCategory,
        catalogSearch,
        catalogTypeFilter,
        setSelectedCategory,
        setCatalogSearch,
        setCatalogTypeFilter,
        fetchCatalogLibrary,
        getCatalogItemsByCategory,
        loadModuleDetails,
        duplicateTemplate,
        copyTemplateToUser,
    } = useTemplateCatalogStore();

    useEffect(() => {
        fetchCatalogLibrary();
    }, []);

    // Build category count map from API data
    const countMap = useMemo(() => {
        const map = {};
        for (const c of categories) {
            map[c.category] = (c.templateCount || 0) + (c.sequenceCount || 0);
        }
        return map;
    }, [categories]);

    // Build section-level counts
    const sectionCountMap = useMemo(() => {
        const map = {};
        for (const section of SECTIONS) {
            let total = 0;
            for (const child of section.children) {
                total += countMap[child.key] || 0;
            }
            map[section.key] = total;
        }
        return map;
    }, [countMap]);

    // Build antd Tree data
    const treeData = useMemo(() => {
        return SECTIONS.map(section => {
            const SectionIcon = section.icon;
            return {
                key: section.key,
                title: (
                    <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {section.label}
                        {sectionCountMap[section.key] > 0 && (
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6, fontWeight: 400 }}>
                                ({sectionCountMap[section.key]})
                            </Text>
                        )}
                    </span>
                ),
                icon: <SectionIcon />,
                selectable: true,
                children: section.children.map(child => {
                    const ChildIcon = child.icon;
                    const count = countMap[child.key] || 0;
                    return {
                        key: child.key,
                        title: (
                            <span>
                                {child.label}
                                {count > 0 && (
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                        ({count})
                                    </Text>
                                )}
                            </span>
                        ),
                        icon: <ChildIcon />,
                        isLeaf: true,
                    };
                }),
            };
        });
    }, [countMap, sectionCountMap]);

    const filteredItems = getCatalogItemsByCategory();

    const handleTreeSelect = useCallback((selectedKeys) => {
        if (selectedKeys.length === 0) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(selectedKeys[0]);
        }
    }, [setSelectedCategory]);

    const handleUse = useCallback((item) => {
        if (item.type === 'multi-step') {
            duplicateTemplate(item.moduleGroupId || item.id, item.title, item.description, item.category);
        } else {
            copyTemplateToUser(item);
        }
    }, [duplicateTemplate, copyTemplateToUser]);

    // Derive the label for the right panel header
    const selectedLabel = useMemo(() => {
        if (!selectedCategory) return 'All Templates';
        if (CATEGORY_LABELS[selectedCategory]) return CATEGORY_LABELS[selectedCategory];
        // Section-level selection
        const section = SECTIONS.find(s => s.key === selectedCategory);
        return section ? section.label : selectedCategory;
    }, [selectedCategory]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Splitter: {
                        splitBarDraggableSize: 20,
                        splitBarSize: 2,
                        splitTriggerSize: 2,
                    },
                },
            }}
        >
            <div style={{ flex: 1, minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                <Splitter style={{ height: '100%', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Left panel: Category tree */}
                    <Splitter.Panel size={280} min={280} resizable={false}>
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            background: '#fafafa',
                        }}>
                            <div style={{
                                padding: '12px 12px 8px',
                                borderBottom: '1px solid #f0f0f0',
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#595959',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}>
                                Categories
                            </div>
                            <style>{`.catalog-tree .ant-tree-switcher { display: none; }`}</style>
                            <div style={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', padding: '8px 4px' }}>
                                <Tree
                                    className="catalog-tree"
                                    showIcon
                                    defaultExpandAll
                                    indent={16}
                                    selectedKeys={selectedCategory ? [selectedCategory] : []}
                                    onSelect={handleTreeSelect}
                                    treeData={treeData}
                                    blockNode
                                    style={{ background: 'transparent' }}
                                />
                            </div>
                        </div>
                    </Splitter.Panel>

                    {/* Right panel: Content list */}
                    <Splitter.Panel>
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            background: '#fff',
                        }}>
                            {/* Header with search + filter */}
                            <div style={{
                                padding: '12px 16px',
                                borderBottom: '1px solid #f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                flexWrap: 'wrap',
                            }}>
                                <div style={{ fontWeight: 600, fontSize: 15, flex: 1, minWidth: 120 }}>
                                    {selectedLabel}
                                    <Text type="secondary" style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                                        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                                    </Text>
                                </div>
                                <Segmented
                                    size="small"
                                    value={catalogTypeFilter}
                                    onChange={setCatalogTypeFilter}
                                    options={[
                                        { label: 'All', value: 'all' },
                                        { label: 'Messages', value: 'message', icon: <MessageOutlined /> },
                                        { label: 'Sequences', value: 'sequence', icon: <BranchesOutlined /> },
                                    ]}
                                />
                                <Input
                                    placeholder="Search..."
                                    prefix={<SearchOutlined />}
                                    value={catalogSearch}
                                    onChange={e => setCatalogSearch(e.target.value)}
                                    allowClear
                                    size="small"
                                    style={{ width: 200 }}
                                />
                            </div>

                            {/* Content */}
                            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                                {catalogLoading ? (
                                    <div style={{ textAlign: 'center', padding: 48 }}>
                                        <Spin size="large" />
                                    </div>
                                ) : filteredItems.length === 0 ? (
                                    <Empty
                                        description={
                                            selectedCategory
                                                ? (catalogSearch
                                                    ? 'No templates match your search in this category.'
                                                    : 'No templates in this category yet.')
                                                : 'Select a category to browse templates.'
                                        }
                                        style={{ marginTop: 64 }}
                                    />
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {filteredItems.map(item => (
                                            <SequenceCard
                                                key={item.id}
                                                item={item}
                                                onUse={handleUse}
                                                loadModules={loadModuleDetails}
                                                isOwner={false}
                                                isAdmin={isAdmin}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Splitter.Panel>
                </Splitter>
            </div>
        </ConfigProvider>
    );
};

export default CatalogBrowser;
